/**
 * A helper class for creating SVG <path> elements without having to remember the commands.
 */
export class SvgPathBuilder {
    /**
     * Creates an SVG path helper.
     */
    constructor() {
        this.commands = [];
    }
    /**
     * Moves the path to given coordinates without drawing a line.
     * @param x
     * @param y
     */
    moveTo(x, y, absolutePositioning = false) {
        return this.addCommand(`${absolutePositioning ? 'M' : 'm'}${x},${y}`);
    }
    /**
     * Draws a straight line to given coordinates.
     * @param x
     * @param y
     */
    drawLineTo(x, y, absolutePositioning = false) {
        return this.addCommand(`${absolutePositioning ? 'L' : 'l'}${x},${y}`);
    }
    /**
     * Draws a horizontal line to a given x coordinate.
     * @param x
     */
    drawHorizontalLineTo(x, absolutePositioning = false) {
        return this.addCommand(`${absolutePositioning ? 'H' : 'h'}${x}`);
    }
    /**
     * Draws a vertical line to a given y coordinate.
     * @param y
     */
    drawVerticalLineTo(y, absolutePositioning = false) {
        return this.addCommand(`${absolutePositioning ? 'V' : 'v'}${y}`);
    }
    /**
     * Draws a horizontal line of a given length to the left of the current position.
     * @param length
     */
    drawLineLeft(length) {
        return this.drawHorizontalLineTo(-length);
    }
    /**
     * Draws a horizontal line of a given length to the right of the current position.
     * @param length
     */
    drawLineRight(length) {
        return this.drawHorizontalLineTo(length);
    }
    /**
     * Draws a vertical line of a given length up from the current position.
     * @param length
     */
    drawLineUp(length) {
        return this.drawVerticalLineTo(-length);
    }
    /**
     * Draws a vertical line of a given length down from the current position.
     * @param length
     */
    drawLineDown(length) {
        return this.drawVerticalLineTo(length);
    }
    /**
     * Draws an arc.
     * @param xRadius The radius of the arc in the x dimension.
     * @param yRadius The radius of the arc in the y dimension.
     * @param x The destination x coordinate.
     * @param y The destination y coordinate.
     * @param sweep True = clockwise, False = counterclockwise.
     */
    drawArcTo(xRadius, yRadius, x, y, sweep = true, absolutePositioning = false) {
        return this.addCommand(`${absolutePositioning ? 'A' : 'a'}${xRadius},${yRadius} 0 0 ${sweep ? 1 : 0} ${x},${y}`);
    }
    /**
     * Adds a top left border radius arc, clockwise from the current position.
     * @param borderRadius
     */
    drawTopLeftBorderRadiusArc(borderRadius) {
        return this.drawArcTo(borderRadius, borderRadius, borderRadius, -borderRadius, true);
    }
    /**
     * Adds a top right border radius arc, clockwise from the current position.
     * @param borderRadius
     */
    drawTopRightBorderRadiusArc(borderRadius) {
        return this.drawArcTo(borderRadius, borderRadius, borderRadius, borderRadius, true);
    }
    /**
     * Adds a bottom left border radius arc, clockwise from the current position.
     * @param borderRadius
     */
    drawBottomLeftBorderRadiusArc(borderRadius) {
        return this.drawArcTo(borderRadius, borderRadius, -borderRadius, -borderRadius, true);
    }
    /**
     * Adds a bottom right border radius arc, clockwise from the current position.
     * @param borderRadius
     */
    drawBottomRightBorderRadiusArc(borderRadius) {
        return this.drawArcTo(borderRadius, borderRadius, -borderRadius, borderRadius, true);
    }
    /**
     * Closes the path to complete the shape.
     */
    closePath() {
        return this.addCommand('z');
    }
    /**
     * Returns the path commands as a string to be used as path data. E.G. <path d={data} />.
     */
    toString() {
        return this.commands.join(' ');
    }
    /**
     * Adds a command to the path.
     * @param command The command, as a string.
     */
    addCommand(command) {
        this.commands.push(command);
        return this;
    }
}
